// RecentWork — Phase 6 close-out visual (Feb 2026 release-check update).
//
// Kaily's release-check directive: "首页使用 Kaily 的真实作品作为主视觉。
// 空作品卡片先隐藏。" — so we no longer render olive "placeholder tombstones"
// to pad the grid to 3. We only render projects that have a real hero image.
// If the published set is empty (e.g. Preview DB), the whole section is
// hidden rather than showing fake slots. In Production (10+ published cases)
// the grid simply shows the real top 3.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// We treat the public /placeholder-case.png asset as "no real hero yet".
const DRAFT_HERO_RE = /\/placeholder-case\.png(\?|$)/i;
const hasRealHero = (p) => !!p?.hero_image && !DRAFT_HERO_RE.test(p.hero_image);

export default function RecentWork() {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/projects`);
                const all = (data || []).filter(hasRealHero);
                const featured = all.filter((p) => p.featured);
                setItems((featured.length ? featured : all).slice(0, 3));
            } catch {
                setItems([]);
            } finally {
                setLoaded(true);
            }
        })();
    }, []);

    // Hide the entire section until we know whether there's real work to show.
    // This avoids a flash of empty tombstones on first paint.
    if (!loaded || items.length === 0) return null;

    return (
        <section id="work" className="pt-16 md:pt-24 pb-20 md:pb-28 bg-cafe-cream" data-testid="recent-work-section">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                {/* Header rail — eyebrow + big heading left, VIEW ALL WORK right */}
                <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
                    <div>
                        <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                            Selected Work
                        </p>
                        <h2 className="font-serif text-4xl md:text-6xl lg:text-[76px] leading-[0.95] text-cafe-espresso text-balance" style={{ fontWeight: 500 }}>
                            Stories you can feel.
                        </h2>
                    </div>
                    <Link
                        to="/work"
                        data-testid="recent-work-view-all"
                        className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.26em] text-cafe-espresso/80 hover:text-cafe-olive border-b border-cafe-espresso/25 hover:border-cafe-olive pb-1 whitespace-nowrap transition-colors mb-2"
                    >
                        View all work
                        <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-testid="recent-work-grid">
                    {items.map((p) => (
                        <WorkCard key={p.slug} project={p} />
                    ))}
                </div>

                <div className="mt-10 flex md:hidden justify-center">
                    <Link
                        to="/work"
                        data-testid="recent-work-view-all-mobile"
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.26em] text-cafe-espresso border-b border-cafe-espresso/40 pb-1"
                    >
                        View all work <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        </section>
    );
}

function WorkCard({ project }) {
    const { title, category, hero_image, slug, in_progress } = project;
    return (
        <div data-testid="work-card" className="group">
            <Link
                to={`/work/${slug}`}
                className="block relative aspect-[16/10] bg-cafe-olive overflow-hidden"
                data-testid={`work-card-link-${slug}`}
            >
                <img
                    src={hero_image}
                    alt={title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
                {in_progress && (
                    <span className="absolute top-3 right-3 inline-flex items-center px-2.5 py-0.5 rounded-full bg-cafe-cream/90 text-cafe-espresso text-[10px] uppercase tracking-[0.18em]">
                        In progress
                    </span>
                )}
            </Link>
            <div className="mt-4 flex items-baseline justify-between">
                <p className="text-[13px] md:text-sm uppercase tracking-[0.22em] font-medium text-cafe-espresso">
                    {title}
                </p>
                <ArrowUpRight className="h-3.5 w-3.5 text-cafe-espresso/60 group-hover:text-cafe-olive transition-colors" strokeWidth={1.5} />
            </div>
            {category && (
                <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-cafe-espresso/55">{category}</p>
            )}
        </div>
    );
}
